import { styled } from '@mui/material'
import L, { LatLngExpression } from 'leaflet'
import icon from 'leaflet/dist/images/marker-icon.png'
import iconShadow from 'leaflet/dist/images/marker-shadow.png'
import { useEffect, useMemo, useState } from 'react'
import { MapContainer, Pane, useMap } from 'react-leaflet'
import { useParams } from 'react-router-dom'
import { IProjectInfo } from 'src/generated-types/project'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'
import { hasRenderableGeometry } from 'src/utils/position-geometry'
import { ILocality, IMooring, IMooringType, IPosition, IPositionType } from '../../../../generated-types'
import { CenterChangeComponent } from './CenterChangeComponent'
import { CreateLayerGroup } from './ComponentLayer'
import { BoatController } from './components/BoatController'
import { DownloadController } from './components/DownloadController'
import { ElectricityLine } from './components/ElectricityLine'
import { EnlargeComponent } from './components/EnlargeIconComponent'
import { GestureHandlingComponent } from './components/GestureHandlingComponent'
import { LocationController } from './components/LocationController'
import { MapControls } from './components/MapControls'
import { BaseTileLayer } from './components/MapStaticContent'
import { getKeyValue, maxZoom } from './constraints'
import { ControlLayer } from './layers/ControlLayer'
import VesselLayers from './layers/VesselLayers'
import MapBoundsComponent from './MapBoundsComponent'
import { IGroupSelector } from './MapComponent'

const MapTestIdSetter = (): null => {
  const map = useMap()
  useEffect(() => {
    const container = map.getContainer()
    const setAttr = (sel: string, id: string) => {
      const el = container.querySelector(sel) as HTMLElement | null
      if (el) el.setAttribute('data-test-id', id)
    }
    const timer = setTimeout(() => {
      setAttr('.leaflet-control-zoom-in', 'map-zoom-in')
      setAttr('.leaflet-control-zoom-out', 'map-zoom-out')
      setAttr('.leaflet-control-layers-toggle', 'map-layer-toggle')
      setAttr('.leaflet-control-layers', 'map-layer-panel')
    }, 0)
    return () => clearTimeout(timer)
  }, [map])
  return null
}

// Power lines saved before positions held their own point list have no geometry, and
// are still drawn as one stitched polyline across the whole map.
const isLegacyElectricityLine = (position: IPosition): boolean =>
  position.type == IPositionType.ElectricityLine && !hasRenderableGeometry(position.geometry, position.type)

const DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
})

L.Marker.prototype.options.icon = DefaultIcon

export interface MapComponentProps {
  positions?: IPosition[]
  moorings?: IMooring[]
  projects?: IProjectInfo[]
  localities?: ILocality[]
  boundsPositions?: IPosition[]
  center?: LatLngExpression
  zoom?: number
  mooringType?: IMooringType
  zoomControl?: boolean
  scrollWheelZoom?: boolean
  layerControl?: boolean
  attributionControl?: boolean
  downloadControl?: boolean
  onClickDownload?: (anchorEl: HTMLElement) => void
  isModalView?: boolean
  navigateLocalityWhenClick?: boolean
  removeAttribution?: boolean
  showEnlargeIcon?: boolean
  onClickEnlarge?: () => void
  showCageStatusPopOver?: boolean
  onPositionSelect?: (positionId: number, positionName: string) => void
  enableBoatController?: boolean
  enableLocationController?: boolean
  enableCluster?: boolean
  enableVessel?: boolean
  customerId?: EntityId
  customUrl?: string
  gestureHandling?: boolean
  readOnly?: boolean
  showCriticalPositions?: boolean
  children?: React.ReactNode
  isSmallMapView?: boolean
  fitAllPositions?: boolean
}

const LeafletMapContainer = ({
  positions,
  moorings,
  localities,
  boundsPositions,
  center,
  zoom = 16,
  mooringType,
  zoomControl = true,
  layerControl = true,
  downloadControl = false,
  showEnlargeIcon: showFullScreenIcon = false,
  attributionControl = true,
  scrollWheelZoom = true,
  onClickDownload,
  onClickEnlarge,
  isModalView = false,
  navigateLocalityWhenClick = false,
  removeAttribution = false,
  showCageStatusPopOver = false,
  onPositionSelect,
  enableBoatController = false,
  enableLocationController = false,
  enableCluster = false,
  enableVessel = false,
  customerId,
  customUrl,
  gestureHandling = true,
  projects,
  readOnly = false,
  showCriticalPositions = false,
  children,
  isSmallMapView = false,
  fitAllPositions = false,
}: MapComponentProps) => {
  const [vessel, setVessel] = useState(enableVessel)
  const { customerId: routeCustomerId } = useParams()
  const resolvedCustomerId = Number(customerId ?? routeCustomerId)
  const aisCustomerId = Number.isFinite(resolvedCustomerId) && resolvedCustomerId > 0 ? resolvedCustomerId : undefined
  const [enableLocality, setEnableLocality] = useState(true)
  const [groupSelector, setGroupSelector] = useState<IGroupSelector>({
    facility: true,
    fleet: true,
    deviation: false,
    barentsWatch: false,
    aquaculture: false,
  })

  let mapPosition = center ?? [67.28, 14.40501]

  if (
    positions &&
    positions[0] &&
    positions[0].latitude &&
    positions[0].longitude &&
    positions[0].latitude !== 0 &&
    positions[0].longitude !== 0
  )
    mapPosition = [positions[0].latitude, positions[0].longitude]

  const [electricityLinePositions, setElectricityLinePositions] = useState<IPosition[]>([])

  useEffect(() => {
    if (positions) {
      setElectricityLinePositions(positions.filter(p => isLegacyElectricityLine(p)))
    }
  }, [positions])

  useEffect(() => {
    if (moorings) {
      let p: IPosition[] = []
      moorings.forEach(m => {
        const pos = m.positions.filter(p => isLegacyElectricityLine(p))
        p = [...p, ...pos]
      })
      setElectricityLinePositions(p)
    }
  }, [moorings])

  useEffect(() => {
    if (projects) {
      let p: IPosition[] = []
      projects.forEach(m => {
        if (m.positions == undefined) return
        const pos = m.positions.filter(p => p.type == IPositionType.ElectricityLine)
        p = [...p, ...pos]
      })
      setElectricityLinePositions(p)
    }
  }, [projects])

  useEffect(() => {
    if (removeAttribution) {
      const attribution = document.getElementsByClassName('leaflet-control-attribution')[0] as HTMLElement
      //attribution.style.display = removeAttribution ? 'none' : 'block'
    }
  }, [removeAttribution])

  useEffect(() => {
    if (localities) {
      let p: IPosition[] = []
      localities.forEach(l => {
        l.moorings.forEach(m => {
          const pos = m.positions.filter(p => isLegacyElectricityLine(p))
          p = [...p, ...pos]
        })
      })
      setElectricityLinePositions(p)
    }
  }, [localities])

  const groupSelectorKeys = useMemo(() => {
    switch (mooringType) {
      case 0:
        return [
          { key: 'fleet', value: 5 },
          { key: 'deviation', value: 13 },
          { key: 'barentsWatch', value: 16 },
          { key: 'aquaculture', value: 21 },
        ]
        break
      case 1:
      case 2:
      case 3:
        return [
          { key: 'facility', value: 5 },
          { key: 'deviation', value: 13 },
          { key: 'barentsWatch', value: 16 },
          { key: 'aquaculture', value: 21 },
        ]
        break
      default:
        return [
          { key: 'facility', value: 5 },
          { key: 'fleet', value: 11 },
          { key: 'deviation', value: 19 },
          { key: 'barentsWatch', value: 22 },
          { key: 'aquaculture', value: 27 },
        ]
        break
    }
  }, [mooringType])

  const items = document.querySelectorAll('leaflet-control-layers-selector')
  useEffect(() => {
    const items = document.querySelectorAll('.leaflet-control-layers-selector')
    if (layerControl && items) {
      groupSelectorKeys.forEach(key => {
        if (items[key.value]) {
          items[key.value].addEventListener('click', () => {
            const checked = (items[key.value] as HTMLInputElement).checked
            setGroupSelector(prev => {
              return {
                ...prev,
                ...getKeyValue(key.key, checked),
              }
            })
          })
        }
      })
    }
    return () => {
      groupSelectorKeys.forEach(key => {
        if (layerControl && items && items[key.value]) {
          items[key.value].removeEventListener('click', () => {
            const checked = (items[key.value] as HTMLInputElement).checked
            setGroupSelector(prev => {
              return {
                ...prev,
                ...getKeyValue(key.key, checked),
              }
            })
          })
        }
      })
    }
  }, [items, groupSelectorKeys])

  useEffect(() => {
    const items = document.querySelectorAll('.leaflet-control-layers-selector')
    if (layerControl && items) {
      groupSelectorKeys.forEach(key => {
        if (items[key.value]) {
          items[key.value].addEventListener('click', () => {
            const checked = (items[key.value] as HTMLInputElement).checked
            setGroupSelector(prev => {
              return {
                ...prev,
                ...getKeyValue(key.key, checked),
              }
            })
          })
        }
      })
    }
    return () => {
      groupSelectorKeys.forEach(key => {
        if (layerControl && items && items[key.value]) {
          items[key.value].removeEventListener('click', () => {
            const checked = (items[key.value] as HTMLInputElement).checked
            setGroupSelector(prev => {
              return {
                ...prev,
                ...getKeyValue(key.key, checked),
              }
            })
          })
        }
      })
    }
  }, [items, groupSelectorKeys])

  return (
    <MapBaseContainer id="controller-div" data-test-id="map-container">
      <MapContainer
        center={(center as LatLngExpression) ?? [0, 0]}
        zoom={zoom}
        zoomDelta={0.25}
        maxZoom={maxZoom}
        zoomControl={zoomControl}
        attributionControl={attributionControl}
        zoomSnap={0}
        scrollWheelZoom={readOnly ? false : scrollWheelZoom}
        dragging={!readOnly}
        doubleClickZoom={!readOnly}
        touchZoom={!readOnly}
        boxZoom={!readOnly}
        keyboard={!readOnly}
        className={`w-full h-full rounded-lg${readOnly ? ' map-readonly' : ''}`}
      >
        <MapTestIdSetter />
        {center && <CenterChangeComponent center={center as LatLngExpression} />}
        {!center && (
          <MapBoundsComponent
            localities={boundsPositions ? undefined : localities}
            moorings={boundsPositions ? undefined : moorings}
            projects={boundsPositions ? undefined : projects}
            positions={boundsPositions ?? positions}
            fitAllPositions={fitAllPositions}
            zoom={zoom}
          />
        )}
        <BaseTileLayer customUrl={customUrl} />
        <Pane name="farms" style={{ zIndex: 404 }} />
        <Pane name="anchorPoint" style={{ zIndex: 404 }} />
        <Pane name="bridle" style={{ zIndex: 404 }} />
        <Pane name="frameLine" style={{ zIndex: 404 }} />
        <Pane name="mooringLine" style={{ zIndex: 404 }} />
        <Pane name="innerMooringLine" style={{ zIndex: 404 }} />
        <Pane name="bridleHitArea" style={{ zIndex: 406 }} />
        <Pane name="electricityLine" style={{ zIndex: 410 }} />
        <Pane name="corner" style={{ zIndex: 409 }} />
        <Pane name="ring" style={{ zIndex: 409 }} />
        <Pane name="net" style={{ zIndex: 409 }} />
        <Pane name="cage" style={{ zIndex: 409 }} />
        <Pane name="polygon" style={{ zIndex: 404 }} />
        <Pane name="anchorPointUpdated" style={{ zIndex: 405 }} />
        <Pane name="bridleUpdated" style={{ zIndex: 405 }} />
        <Pane name="frameLineUpdated" style={{ zIndex: 405 }} />
        <Pane name="mooringLineUpdated" style={{ zIndex: 405 }} />
        <Pane name="innerMooringLineUpdated" style={{ zIndex: 405 }} />
        <Pane name="electricityLineUpdated" style={{ zIndex: 412 }} />
        <Pane name="cornerUpdated" style={{ zIndex: 410 }} />
        <Pane name="ringUpdated" style={{ zIndex: 410 }} />
        <Pane name="netUpdated" style={{ zIndex: 410 }} />
        <Pane name="cageUpdated" style={{ zIndex: 410 }} />
        <Pane name="polygonUpdated" style={{ zIndex: 405 }} />
        <Pane name="barents-watch-zones" style={{ zIndex: 500 }} />
        <Pane name="circlePoints" style={{ zIndex: 600 }} />
        <Pane name="layoutEditor" style={{ zIndex: 620 }} />
        {layerControl ? (
          <ControlLayer
            localities={localities as ILocality[]}
            positions={positions as IPosition[]}
            moorings={moorings as IMooring[]}
            projects={projects as IProjectInfo[]}
            mooringType={mooringType}
            zoom={zoom}
            center={center}
            groupSelector={groupSelector}
            navigateLocalityWhenClick={navigateLocalityWhenClick}
            isModalView={isModalView as boolean}
            showCageStatusPopOver={showCageStatusPopOver}
            onPositionSelect={onPositionSelect}
            enableCluster={enableCluster}
            enableLocality={enableLocality}
            showCriticalPositions={showCriticalPositions}
          />
        ) : (
          <CreateLayerGroup
            positions={positions as IPosition[]}
            moorings={moorings as IMooring[]}
            localities={localities as ILocality[]}
            mapPosition={mapPosition}
            steelFrame={mooringType == IMooringType.SteelCage}
            isFleet={mooringType == IMooringType.Barge}
            isModalView={isModalView}
            mooringType={mooringType}
            showCageStatusPopOver={showCageStatusPopOver}
            onPositionSelect={onPositionSelect}
            projects={projects as IProjectInfo[]}
            showCriticalPositions={showCriticalPositions}
            isSmallMapView={isSmallMapView}
          />
        )}
        {showFullScreenIcon && <EnlargeComponent onClick={onClickEnlarge} />}
        {enableVessel && vessel && <VesselLayers customerId={aisCustomerId} />}
        {electricityLinePositions.length > 0 && <ElectricityLine polygonPositions={electricityLinePositions} />}
        {downloadControl && <DownloadController onClick={onClickDownload} />}
        {enableLocationController && (
          <LocationController
            onClick={() => {
              setEnableLocality(isLocality => !isLocality)
            }}
          />
        )}
        {enableBoatController && (
          <BoatController
            enable={vessel}
            onClick={() => {
              setVessel(vesselData => !vesselData)
            }}
          />
        )}
        {gestureHandling && <GestureHandlingComponent />}
        {zoomControl && <MapControls />}
        {children}
      </MapContainer>
    </MapBaseContainer>
  )
}

export const MapBaseContainer = styled('div')(({ theme }) => ({
  width: '100%',
  height: '100%',
  '& .leaflet-control-layers-separator': {
    display: 'none',
  },
  '& .leaflet-control-layers-expanded': {
    '& .leaflet-control-layers-list': {
      display: 'flex',
      flexDirection: 'column-reverse',
    },
    '& .leaflet-control-layers-overlays': {
      '& label': {
        '& .leaflet-control-layers-separator': {
          display: 'box',
        },
      },
      '& label:last-child': {
        '& input': {
          display: 'none',
        },
      },
    },
  },
  '& .leaflet-control-layers-toggle': {
    backgroundImage: `url(${'/icons/map-layers.svg'})`,
    backgroundColor: getThemeColor(theme, MUIThemeColors.white),
    height: '30px',
    width: '30px',
    padding: '20px',
    borderRadius: '50%',
  },
  '& .leaflet-control-zoom-in, .leaflet-control-zoom-out': {
    color: getThemeColor(theme, MUIThemeColors.primaryLight),
    backgroundColor: getThemeColor(theme, MUIThemeColors.white),
  },
  '& .leaflet-control-zoom-in:hover, .leaflet-control-zoom-out:hover': {
    color: getThemeColor(theme, MUIThemeColors.primaryMain),
    backgroundColor: getThemeColor(theme, MUIThemeColors.secondaryLight),
  },
  '& .leaflet-top.leaflet-left': {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'flex-start',
    gap: '1px',
  },
  '& .leaflet-ruler, .leaflet-ruler-clicked': {
    height: '44px !important',
    width: '44px !important',
    backgroundSize: '22px',
    zIndex: '9999 !important',
  },
  '& .result-tooltip, .moving-tooltip': {
    zIndex: '9999 !important',
  },
  '& .leaflet-container, .leaflet-container:focus': {
    outline: 'none',
  },
  '& .ais-vessel-icon': {
    background: 'transparent',
    border: 'none',
  },
  '& .map-readonly': {
    pointerEvents: 'none',
    '& .leaflet-pane': {
      pointerEvents: 'none',
    },
  },
}))

export default LeafletMapContainer
